import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"
import { Search, PencilSquare, Trash, PlusCircle, Backspace, Diagram3, Wallet, Copy } from "react-bootstrap-icons" // Iconos de Bootstrap
import { AlertError, AlertLoading } from '../helpers/AlertHelper'
import { formatDate } from "../helpers/momentHelper"
import formatCurrency from "../helpers/currencyHelper"
import FormModal from "../components/modals/FormModal"
import TableManagementThreeButtons from "../components/tables/TableManagementThreeButtoms";
import SearchSelect from '../components/form/SearchSelect'
import IncomesService from "../services/IncomesService"
import ProjectService from "../services/ProjectsService"
import PartnersService from "../services/PartnersService"
import IncomesForm from "./IncomesForm"

const Incomes = ({ showSearchBar = true, selectedProject: selectedProjectProp, selectedPartner: selectedPartnerProp, yearFrom: yearFromProp, yearTo: yearToProp }) => {
    const intl = useIntl()
    const [incomeId, setIncomeId] = useState(null)
    const [incomes, setIncomes] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("date")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)
    const [yearFrom, setYearFrom] = useState('')
    const [yearTo, setYearTo] = useState('')

    const [projectsList, setProjectsList] = useState([])
    const [selectedProject, setSelectedProject] = useState('')
    const [partnersList, setPartnersList] = useState([])
    const [selectedPartner, setSelectedPartner] = useState('')

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch projects
    const fetchProjects = async () => {
        try {
            const response = await ProjectService.getProjects(1, '', 2000)
            setProjectsList(response.data.projects)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch partners
    const fetchPartners = async () => {
        try {
            const response = await PartnersService.getPartners(1, '', 2000)
            setPartnersList(response.data.partners)
        } catch (err) {
            setError(err)
        }
    }

    // Fetch incomes
    const fetchIncomes = async () => {
        try {
            const response = await IncomesService.getIncomes(currPage, search, perPage, sortBy, sortOrder, selectedProject, selectedPartner, yearFrom, yearTo)
            setIncomes(response.data.incomes)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchIncomes()
    }

    const handleSelectProjectChange = (e) => {
        setSelectedProject(e.target.value)
        setCurrPage(1)
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setSelectedProject('')
        setSelectedPartner('')
        setYearFrom('')
        setYearTo('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    useEffect(() => {
        setSelectedProject(selectedProjectProp || '')
    }, [selectedProjectProp])

    useEffect(() => {
        setSelectedPartner(selectedPartnerProp || '')
    }, [selectedPartnerProp])

    useEffect(() => {
        setYearFrom(yearFromProp || '')
    }, [yearFromProp])

    useEffect(() => {
        setYearTo(yearToProp || '')
    }, [yearToProp])

    // fetch projects when start
    useEffect(() => {
        fetchProjects()
        fetchPartners()
    }, [])

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchIncomes()
    }, [currPage, perPage, search, sortBy, sortOrder, selectedProject, selectedPartner, yearFrom, yearTo])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="incomesAdd" defaultMessage="Add income" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="incomesEdit" defaultMessage="Edit income" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="incomesDelete" defaultMessage="Delete income" />,
                icon: <Trash />,
            },
            duplicate: {
                title: <FormattedMessage id="textDuplicate" defaultMessage="Duplicate expense" />,
                icon: <Copy />,
            },
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setIncomeId(type === 'add' ? false : data._id) // Si 'add', incomeid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'projectId',
            name: <FormattedMessage id="incomesProject" defaultMessage="Project" />,
            selector: (row) => row.projectId.name,
            sortable: true,
        },
        {
            id: 'concept',
            name: <FormattedMessage id="incomesConcept" defaultMessage="Concept" />,
            selector: (row) => row.concept,
        },
        {
            id: 'estimatedDate',
            name: <FormattedMessage id="incomesEstimatedDate" defaultMessage="Estimated date" />,
            selector: (row) => formatDate(row.estimatedDate, 'DD/MM/YYYY'),
            sortable: true,
        },
        {
            id: 'date',
            name: <FormattedMessage id="incomesDate" defaultMessage="Income date" />,
            selector: (row) => formatDate(row.date, 'DD/MM/YYYY'),
            sortable: true,
        },
        {
            id: 'amount',
            name: <FormattedMessage id="incomesAmount" defaultMessage="Amount" />,
            selector: (row) => formatCurrency(row.amount),
            sortable: true,
            right: "true",
        }
    ]

    return (
        <Container fluid className="p-0">
            {showSearchBar && (
                <Row className="p-1 bg-secondary">
                    <Col>
                        <InputGroup>
                            <InputGroup.Text><Diagram3 /></InputGroup.Text>
                            <SearchSelect
                                fieldName={'projectId'}
                                selected={selectedProject}
                                onChange={handleSelectProjectChange}
                                options={projectsList}
                            />
                            <InputGroup.Text><Search /></InputGroup.Text>

                            {/* 
                            <SearchSelect
                                fieldName={'partnerId'}
                                selected={selectedPartner}
                                onChange={handleSelectPartnerChange}
                                options={partnersList}
                            />
                            <InputGroup.Text><Search /></InputGroup.Text>
*/}
                            <Form.Control
                                type="text"
                                placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search incomes..." })}
                                value={search}
                                onChange={handleSearchChange}
                            />
                            <Button onClick={handleSearchClean} variant="light">
                                <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                            </Button>

                            <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                                <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                            </Button>
                        </InputGroup>
                    </Col>
                </Row>
            )}

            {isLoaded ? (
                <TableManagementThreeButtons
                    icon={<Wallet />}
                    title={<FormattedMessage id="navProjectIncomes" defaultMessage="Projects Incomes" />}
                    columns={columns}
                    data={incomes}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={IncomesForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={incomeId}
                action={modalAction} />

        </Container>
    )
}

export default Incomes